import React, { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, TextField, DialogActions,
  Button, FormControl, InputLabel, ListItemText, MenuItem,
  OutlinedInput, Select, Stack, Skeleton, Checkbox, Typography
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { useRecoilState } from "recoil";
import { CacheService } from "../../common/services/cache.service";
import environment from "../../environment/environment";
import APIService from "../../services/api.service";
import { User } from "../../models/user";
import { tokenState } from "../../atoms/token";

const roleAliases = {
  'etic_admin': 'Technical Information Center',
  'etic_sensitive_view': 'Sensitive',
  'etic_archaeology_view': 'Archaeology'
};

const aliasToRole = Object.fromEntries(
  Object.entries(roleAliases).map(([k, v]) => [v, k])
);

export function UserPermissionDialog({ userId, handleSaveClick, handleCloseClick }) {
  const [open] = useState(true);
  const [userRoles, setUserRoles] = useState([]);
  const [user, setUser] = useState(null);
  const [token] = useRecoilState(tokenState);
  const roles = Object.values(roleAliases);

  useEffect(() => {
    const fetchUser = async () => {
      const query = userId ? `get?userid=${userId}` : 'current';
      const url = `${environment.npgApiUrl}user/${query}`;
      const apiService = new APIService(axios, new CacheService, url, token);

      try {
        const res = await apiService.getAnyAsync<User>();
        const filteredRoles = (res.roles || []).filter(r => r.startsWith('etic'));
        const aliases = filteredRoles.map(r => roleAliases[r]).filter(Boolean);
        setUser({ ...res, roles: aliases });
        setUserRoles(aliases);
      } catch (err) {
        console.error("Failed to fetch user info:", err);
      }
    };

    fetchUser();
  }, [userId]);

  const handleRoleChange = (event) => {
    const value = event.target.value;
    setUserRoles(typeof value === 'string' ? value.split(',') : value);
  };

  const isLoading = !user && !user?.firstName && !user?.name;

  return (
    <Dialog open={open} PaperProps={{ style: { minWidth: "900px" } }}>
    <DialogTitle sx={{ fontWeight: "bolder" }}>User Permissions</DialogTitle>
    <DialogContent>
      {isLoading ? (
        <>
          <Stack spacing={2} direction="row" sx={{ my: 2 }}>
            <Skeleton height={56} width={600} sx={{ borderRadius: 1 }} />
            <Skeleton height={56} width={600} sx={{ borderRadius: 1 }} />
          </Stack>
          <Skeleton height={56} width={420} sx={{ borderRadius: 1, mb: 2 }} />
          <Typography variant="h6" gutterBottom>Edit User Permissions</Typography>
          <Skeleton height={56} width={500} sx={{ borderRadius: 1 }} />
        </>
      ) : (
        <>
          <Stack spacing={2} direction="row" sx={{ my: 2 }}>
            <TextField
              label="Name"
              value={user?.firstName && user?.lastName ? `${user?.firstName} ${user?.lastName}` : user?.name ? `${user?.name}` : ''}
              disabled
              size="small"
              sx={{ width: 600 }}
            />
            <TextField
              label="Email"
              value={user.email}
              disabled
              size="small"
              sx={{ width: 600 }}
            />
          </Stack>

          <TextField
            label="Permissions"
            value={user.roles.join(', ')}
            disabled
            size="small"
            sx={{ width: 420, mb: 2 }}
          />

          <Typography variant="h6" gutterBottom>Edit User Permissions</Typography>

          <FormControl sx={{ width: 500 }}>
            <InputLabel id="permissions-label">Permissions</InputLabel>
            <Select
              labelId="permissions-label"
              multiple
              value={userRoles}
              onChange={handleRoleChange}
              input={<OutlinedInput label="Permissions" />}
              renderValue={(selected) => selected.join(', ')}
            >
              {roles.map((role) => (
                <MenuItem key={role} value={role}>
                  <Checkbox checked={userRoles.includes(role)} />
                  <ListItemText primary={role} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}
    </DialogContent>

    <DialogActions>
      <Button variant="outlined" startIcon={<FontAwesomeIcon icon={["fas", "circle-x"]} />} onClick={handleCloseClick}>
        Cancel
      </Button>
      <Button
        variant="contained"
        startIcon={<FontAwesomeIcon icon={["fas", "save"]} />}
        disabled={!user?.userId}
        onClick={() => handleSaveClick(userRoles, user.userId)}
      >
        Save
      </Button>
    </DialogActions>
  </Dialog>
  );
}
